import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  FaCheckCircle,
  FaClock,
  FaEuroSign,
  FaExclamationTriangle,
  FaFileInvoiceDollar,
  FaHistory,
  FaPlus,
  FaSyncAlt,
} from "react-icons/fa";

import useScrollToTop from "../../../hooks/useScollToTop";
import DashboardNavigation from "../../DashboardNavigation/DashboardNavigation";
import { useUser } from "../../Home/UserContext";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { getRoleFromRoleId } from "../../../utils/roleHelper";

import HeaderTitle from "../../../components/HeaderTitle/HeaderTitle";
import ScrollIndicator from "../../Home/ScrollIndicator";

import {
  getExpenseProfileByUser,
  upsertExpenseProfileByUser,
  createExpenseClaim,
  updateExpenseClaim,
  computeExpenseDrivingDistance,
  getExpenseClaimsByUser,
} from "../../../services/expenseServices";

import ExpenseProfileCard from "./components/ExpenseProfileCard";
import EventSelectCard from "./components/EventSelectCard";
import ExpenseMetaCard from "./components/ExpenseMetaCard";
import IndemnitiesCard from "./components/IndemnitiesCard";
import ExpenseItemsCard from "./components/ExpenseItemsCard";
import ExpenseAttachmentsCard from "./components/ExpenseAttachmentsCard";
import ClaimSummaryCard from "./components/ClaimSummaryCard";
import UserExpenseClaimsHistoryCard from "./components/UserExpenseClaimsHistoryCard";

const EMPTY_PROFILE = {
  nom: "",
  prenom: "",
  adresse: "",
  cp: "",
  ville: "",
  fonction: "",
  telephone: "",
  email: "",
};

const toStr = (value) => (value == null ? "" : String(value));

const nil = (value) =>
  typeof value === "string"
    ? value.trim() === ""
      ? null
      : value.trim()
    : value;

const numOrNull = (value) => {
  if (value === null || value === undefined || value === "") return null;

  const n = Number(String(value).replace(",", "."));

  return Number.isFinite(n) ? n : null;
};

function cn(...classes) {
  return classes.filter(Boolean).join(" ");
}

function getClaimStats(claims = []) {
  const total = claims.length;
  const draft = claims.filter((claim) => claim.statut === "draft").length;
  const submitted = claims.filter(
    (claim) => claim.statut === "submitted",
  ).length;
  const approved = claims.filter((claim) => claim.statut === "approved").length;
  const paid = claims.filter((claim) => claim.statut === "paid").length;
  const rejected = claims.filter((claim) => claim.statut === "rejected").length;

  const totalAmount = claims.reduce((sum, claim) => {
    const amount = Number(claim?.total_general || 0);
    return Number.isFinite(amount) ? sum + amount : sum;
  }, 0);

  return {
    total,
    draft,
    submitted,
    approved,
    paid,
    rejected,
    totalAmount,
  };
}

function formatAmount(value) {
  return new Intl.NumberFormat("fr-FR", {
    style: "currency",
    currency: "EUR",
  }).format(Number(value || 0));
}

function ExpenseStatsHero({ stats, loading, onRefresh }) {
  const cards = [
    {
      label: "Total fiches",
      value: stats.total,
      icon: FaFileInvoiceDollar,
      className: "from-blue-600 via-indigo-600 to-slate-950 text-white",
    },
    {
      label: "En attente",
      value: stats.submitted,
      icon: FaClock,
      className: "from-amber-500 via-orange-500 to-slate-950 text-white",
    },
    {
      label: "Validées / payées",
      value: stats.approved + stats.paid,
      icon: FaCheckCircle,
      className: "from-emerald-500 via-green-600 to-slate-950 text-white",
    },
    {
      label: "Montant total",
      value: formatAmount(stats.totalAmount),
      icon: FaEuroSign,
      className: "from-violet-500 via-indigo-600 to-slate-950 text-white",
    },
  ];

  return (
    <section className="overflow-hidden rounded-[2rem] border border-white/70 bg-white shadow-xl shadow-slate-200/70 dark:border-slate-800/80 dark:bg-slate-900 dark:shadow-black/20">
      <div className="relative overflow-hidden bg-gradient-to-br from-[#013E8E] via-indigo-700 to-slate-950 px-4 py-5 text-white sm:px-6 lg:px-7">
        <div className="absolute -right-20 -top-20 h-56 w-56 rounded-full bg-white/10 blur-3xl" />
        <div className="absolute -bottom-24 left-10 h-64 w-64 rounded-full bg-[#4CA82F]/25 blur-3xl" />

        <div className="relative flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="max-w-3xl">
            <p className="text-xs font-bold uppercase tracking-[0.24em] text-white/65">
              Déplacements enseignants
            </p>

            <h2 className="mt-2 text-2xl font-bold tracking-tight sm:text-3xl">
              Fiches de frais
            </h2>

            <p className="mt-2 text-sm leading-6 text-white/78 sm:text-base">
              Créez une fiche, ajoutez vos frais, joignez vos justificatifs et
              suivez l’avancement de vos demandes.
            </p>
          </div>

          <button
            type="button"
            onClick={onRefresh}
            disabled={loading}
            className="inline-flex min-h-[46px] items-center justify-center gap-2 rounded-2xl bg-white/12 px-4 py-3 text-sm font-bold text-white ring-1 ring-white/20 transition hover:bg-white/18 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <FaSyncAlt className={loading ? "animate-spin" : ""} />
            Actualiser
          </button>
        </div>
      </div>

      <div className="grid gap-3 p-3 sm:grid-cols-2 sm:p-4 xl:grid-cols-4">
        {cards.map((card) => {
          const Icon = card.icon;

          return (
            <div
              key={card.label}
              className={cn(
                "overflow-hidden rounded-[1.5rem] bg-gradient-to-br p-4 shadow-sm",
                card.className,
              )}
            >
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-xs font-bold uppercase tracking-[0.18em] text-white/65">
                    {card.label}
                  </p>

                  <p className="mt-2 text-2xl font-bold">{card.value}</p>
                </div>

                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-white/15 text-lg ring-1 ring-white/15">
                  <Icon />
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function LoginRequiredCard({ uid, token }) {
  return (
    <div className="overflow-hidden rounded-[2rem] border border-amber-200 bg-white shadow-xl shadow-slate-200/70 dark:border-amber-500/20 dark:bg-slate-900 dark:shadow-black/20">
      <div className="h-1.5 bg-gradient-to-r from-amber-400 via-orange-500 to-red-500" />

      <div className="p-5 sm:p-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start">
          <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-amber-50 text-xl text-amber-700 ring-1 ring-amber-100 dark:bg-amber-500/10 dark:text-amber-300 dark:ring-amber-500/20">
            <FaExclamationTriangle />
          </span>

          <div>
            <h3 className="text-lg font-bold text-slate-900 dark:text-white">
              Connexion requise
            </h3>

            <p className="mt-2 text-sm leading-6 text-slate-600 dark:text-slate-300">
              Vous devez être connecté pour créer ou consulter vos fiches de
              frais.
            </p>

            <p className="mt-2 text-xs text-slate-400 dark:text-slate-500">
              uid={String(uid || "null")} / token={token ? "ok" : "absent"}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function FicheFraisPage() {
  useScrollToTop();

  const addNotification = useCallback((message, type = "info") => {
    if (type === "success") {
      toast.success(message);
      return;
    }

    if (type === "error") {
      toast.error(message);
      return;
    }

    if (type === "warning" || type === "warn") {
      toast.warning(message);
      return;
    }

    toast.info(message);
  }, []);

  const { user } = useUser();

  const userRole = getRoleFromRoleId(user?.role_id);

  const stored = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("userInfo") || "null");
    } catch {
      return null;
    }
  }, []);

  const [activeTab, setActiveTab] = useState("form");

  const [expenseDraft, setExpenseDraft] = useState({
    event_id: null,
    titre_event: "",
    start_event: null,
    lieu_event: "",

    code_budgetaire_id: null,

    trajet_necessaire: false,
    moyen_transport: null,
    aller_retour: true,
    depart: "",
    arrivee: "",
    distance_km: "",

    covoiturage: false,
    covoiturage_role: "",
    covoiturage_nb_passagers: 0,
    covoiturage_avec: "",

    indemnite_tenue: "0",
    indemnite_representation: "0",
    commentaire: "",
  });

  const uid = user?.userId || user?.id || stored?.userId || stored?.id;
  const token = localStorage.getItem("token");

  const [loadingProfile, setLoadingProfile] = useState(true);
  const [savingProfile, setSavingProfile] = useState(false);
  const [profile, setProfile] = useState(null);
  const [draft, setDraft] = useState(EMPTY_PROFILE);

  const [claimId, setClaimId] = useState(null);
  const [savingClaim, setSavingClaim] = useState(false);
  const [itemsForAttach, setItemsForAttach] = useState([]);

  const [computingDistance, setComputingDistance] = useState(false);

  const [myClaims, setMyClaims] = useState([]);
  const [loadingMyClaims, setLoadingMyClaims] = useState(false);

  const stats = useMemo(() => getClaimStats(myClaims), [myClaims]);

  const isConnected = !!uid && !!token;

  const buildResidenceAddress = () =>
    [draft.adresse, `${draft.cp} ${draft.ville}`]
      .filter(Boolean)
      .join(", ")
      .trim();

  const buildEventAddress = () =>
    [expenseDraft.arrivee || expenseDraft.lieu_event]
      .filter(Boolean)
      .join(", ")
      .trim();

  const prefillFromUser = useCallback(() => {
    const baseNom = user?.nom || stored?.nom || "";
    const basePrenom = user?.prenom || stored?.prenom || "";
    const baseEmail = user?.email || stored?.email || "";

    setDraft((prev) => ({
      ...prev,
      nom: prev.nom || baseNom,
      prenom: prev.prenom || basePrenom,
      email: prev.email || baseEmail,
    }));
  }, [stored, user]);

  const loadProfile = useCallback(async () => {
    if (!uid || !token) {
      setLoadingProfile(false);
      return;
    }

    try {
      setLoadingProfile(true);

      const data = await getExpenseProfileByUser(uid);

      setProfile(data || null);

      if (data) {
        setDraft({
          nom: toStr(data.nom),
          prenom: toStr(data.prenom),
          adresse: toStr(data.adresse),
          cp: toStr(data.cp),
          ville: toStr(data.ville),
          fonction: toStr(data.fonction),
          telephone: toStr(data.telephone),
          email: toStr(data.email),
        });
      } else {
        setDraft(EMPTY_PROFILE);
        prefillFromUser();
      }
    } catch (error) {
      console.error(error);

      addNotification(
        "Impossible de charger vos coordonnées de frais.",
        "error",
      );

      setProfile(null);
      setDraft(EMPTY_PROFILE);
      prefillFromUser();
    } finally {
      setLoadingProfile(false);
    }
  }, [uid, token, addNotification, prefillFromUser]);

  const loadMyClaims = useCallback(async () => {
    if (!uid || !token) {
      setMyClaims([]);
      setLoadingMyClaims(false);
      return;
    }

    try {
      setLoadingMyClaims(true);

      const data = await getExpenseClaimsByUser(uid);

      setMyClaims(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error(error);

      addNotification("Impossible de charger vos fiches de frais.", "error");

      setMyClaims([]);
    } finally {
      setLoadingMyClaims(false);
    }
  }, [uid, token, addNotification]);

  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  useEffect(() => {
    loadMyClaims();
  }, [loadMyClaims]);

  const handleComputeDistance = async () => {
    const originAddress = buildResidenceAddress();
    const destinationAddress = buildEventAddress();

    if (!originAddress || !destinationAddress) {
      addNotification("Adresse de départ ou d’arrivée incomplète.", "error");
      return;
    }

    try {
      setComputingDistance(true);

      const result = await computeExpenseDrivingDistance({
        originAddress,
        destinationAddress,
      });

      setExpenseDraft((prev) => ({
        ...prev,
        depart: prev.depart || `${draft.cp} ${draft.ville}`.trim(),
        arrivee: prev.arrivee || expenseDraft.lieu_event || "",
        distance_km: String(result.distance_km ?? ""),
      }));

      addNotification(
        `Distance calculée : ${result.distance_km} km (${result.duration_text})`,
        "success",
      );
    } catch (error) {
      console.error(error);

      addNotification(
        error?.response?.data?.message || "Impossible de calculer la distance.",
        "error",
      );
    } finally {
      setComputingDistance(false);
    }
  };

  const handleSaveProfile = async () => {
    if (!uid || !token) {
      addNotification("Session expirée, veuillez vous reconnecter.", "error");
      return;
    }

    const required = ["nom", "prenom", "adresse", "cp", "ville"];

    for (const field of required) {
      if (!String(draft[field] || "").trim()) {
        addNotification(`Champ requis : ${field}`, "error");
        return;
      }
    }

    try {
      setSavingProfile(true);

      const saved = await upsertExpenseProfileByUser(uid, {
        nom: nil(draft.nom),
        prenom: nil(draft.prenom),
        adresse: nil(draft.adresse),
        cp: nil(draft.cp),
        ville: nil(draft.ville),
        fonction: nil(draft.fonction),
        telephone: nil(draft.telephone),
        email: nil(draft.email),
      });

      setProfile(saved);

      addNotification("Coordonnées enregistrées ✅", "success");
    } catch (error) {
      console.error(error);

      addNotification(
        "Erreur lors de l’enregistrement des coordonnées.",
        "error",
      );
    } finally {
      setSavingProfile(false);
    }
  };

  const buildClaimPayload = () => {
    const d = expenseDraft;

    const trajet = !!d.trajet_necessaire;
    const moyen = trajet ? d.moyen_transport || "voiture" : null;

    const cov = moyen === "voiture" ? !!d.covoiturage : false;

    const role = cov ? String(d.covoiturage_role || "").trim() : null;
    const avec = cov ? String(d.covoiturage_avec || "").trim() : null;

    return {
      user_id: uid,
      event_id: d.event_id,
      code_budgetaire_id: numOrNull(d.code_budgetaire_id),

      statut: "draft",

      trajet_necessaire: trajet,
      moyen_transport: moyen,
      aller_retour: trajet ? !!d.aller_retour : true,
      depart: trajet ? String(d.depart || "").trim() || null : null,
      arrivee: trajet ? String(d.arrivee || "").trim() || null : null,
      distance_km: trajet ? numOrNull(d.distance_km) : null,

      covoiturage: cov,
      covoiturage_role: cov ? role : null,
      covoiturage_nb_passagers:
        cov && role === "Conducteur"
          ? Math.max(0, Math.floor(Number(d.covoiturage_nb_passagers ?? 0)))
          : 0,
      covoiturage_avec: cov ? avec : null,

      indemnite_tenue: numOrNull(d.indemnite_tenue) ?? 0,
      indemnite_representation: numOrNull(d.indemnite_representation) ?? 0,

      commentaire: (d.commentaire || "").trim() || null,
    };
  };

  const saveClaim = async () => {
    if (!uid || !token) {
      addNotification("Session expirée, veuillez vous reconnecter.", "error");
      return;
    }

    if (!expenseDraft.event_id) {
      addNotification(
        "Sélectionne un événement avant d’enregistrer la fiche.",
        "error",
      );
      return;
    }

    try {
      const payload = buildClaimPayload();

      if (payload.covoiturage) {
        if (
          payload.covoiturage_role !== "Conducteur" &&
          payload.covoiturage_role !== "Passager"
        ) {
          addNotification(
            "Choisis un rôle covoiturage : Conducteur ou Passager.",
            "error",
          );
          return;
        }

        if (!payload.covoiturage_avec) {
          addNotification("Indique avec qui tu covoitures.", "error");
          return;
        }
      }

      setSavingClaim(true);

      const saved = claimId
        ? await updateExpenseClaim(claimId, payload)
        : await createExpenseClaim(payload);

      if (saved?.id) {
        setClaimId(saved.id);
      }

      addNotification("Fiche enregistrée ✅", "success");

      await loadMyClaims();
    } catch (error) {
      console.error(error?.response?.data || error);

      addNotification(
        error?.response?.data?.message ||
          "Erreur lors de l’enregistrement de la fiche.",
        "error",
      );
    } finally {
      setSavingClaim(false);
    }
  };

  const tabButtonClass = (active) =>
    cn(
      "inline-flex min-h-[46px] items-center justify-center gap-2 rounded-2xl px-4 py-2.5 text-sm font-bold transition",
      active
        ? "bg-[#013E8E] text-white shadow-sm hover:bg-[#012f6d] dark:bg-indigo-500 dark:hover:bg-indigo-400"
        : "border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800",
    );

  return (
    <section className="min-h-screen bg-slate-50 text-slate-950 dark:bg-slate-950 dark:text-white">
      <ScrollIndicator />

      <div className="pt-[96px] sm:pt-[120px] lg:pt-[160px]" aria-hidden />

      <main className="mx-auto w-full max-w-7xl px-3 pb-24 sm:px-6 lg:px-8">
        <HeaderTitle
          role="Enseignant"
          title="Fiches de frais"
          subtitle="Déclarez vos frais de déplacement, repas, péage ou hébergement."
          icon={FaFileInvoiceDollar}
          align="left"
          className="mb-4"
        />

        <div className="mb-5 sm:mb-7">
          <DashboardNavigation role={userRole} />
        </div>

        {!isConnected ? (
          <LoginRequiredCard uid={uid} token={token} />
        ) : (
          <>
            <ExpenseStatsHero
              stats={stats}
              loading={loadingMyClaims}
              onRefresh={loadMyClaims}
            />

            <div className="mt-4 overflow-hidden rounded-[2rem] border border-white/70 bg-white/85 shadow-xl shadow-slate-200/70 backdrop-blur dark:border-slate-800/80 dark:bg-slate-900/70 dark:shadow-black/20 sm:mt-6">
              <div className="border-b border-slate-200/70 bg-white/70 px-3 py-3 dark:border-slate-800 dark:bg-slate-900/60 sm:px-5">
                <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                  <div>
                    <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-slate-400 dark:text-slate-500">
                      Espace frais
                    </p>

                    <h3 className="mt-1 text-lg font-bold text-slate-900 dark:text-white">
                      {activeTab === "form"
                        ? "Nouvelle fiche"
                        : "Historique de mes fiches"}
                    </h3>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      className={tabButtonClass(activeTab === "form")}
                      onClick={() => setActiveTab("form")}
                    >
                      <FaPlus />
                      Nouvelle fiche
                    </button>

                    <button
                      type="button"
                      className={tabButtonClass(activeTab === "history")}
                      onClick={() => setActiveTab("history")}
                    >
                      <FaHistory />
                      Mes fiches
                      <span
                        className={cn(
                          "ml-1 rounded-full px-2 py-0.5 text-xs font-bold",
                          activeTab === "history"
                            ? "bg-white/18 text-white"
                            : "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200",
                        )}
                      >
                        {myClaims.length}
                      </span>
                    </button>
                  </div>
                </div>
              </div>

              <div className="p-3 sm:p-5 lg:p-6">
                {activeTab === "form" ? (
                  <div className="space-y-5">
                    <ExpenseProfileCard
                      loading={loadingProfile}
                      saving={savingProfile}
                      profile={profile}
                      draft={draft}
                      setDraft={setDraft}
                      onSave={handleSaveProfile}
                      onReload={loadProfile}
                    />

                    <EventSelectCard
                      value={expenseDraft.event_id}
                      onChange={(payload) => {
                        setClaimId(null);

                        setExpenseDraft((prev) => ({
                          ...prev,
                          event_id: payload.event_id,
                          titre_event: payload.titre,
                          start_event: payload.start,
                          lieu_event: payload.lieu_event,
                          arrivee:
                            prev.arrivee || payload.lieu_event || prev.arrivee,
                        }));
                      }}
                    />

                    <ExpenseMetaCard
                      eventSelected={
                        expenseDraft.event_id
                          ? {
                              event_id: expenseDraft.event_id,
                              titre: expenseDraft.titre_event,
                              start: expenseDraft.start_event,
                              lieu_event: expenseDraft.lieu_event,
                            }
                          : null
                      }
                      draft={expenseDraft}
                      setDraft={setExpenseDraft}
                      onComputeDistance={handleComputeDistance}
                      computingDistance={computingDistance}
                      residenceAddress={buildResidenceAddress()}
                    />

                    <IndemnitiesCard
                      draft={expenseDraft}
                      setDraft={setExpenseDraft}
                      saving={savingClaim}
                      onSave={saveClaim}
                      disabled={!expenseDraft.event_id}
                    />

                    <ExpenseItemsCard
                      claimId={claimId}
                      onEnsureClaim={saveClaim}
                      onItemsChange={setItemsForAttach}
                      onChanged={loadMyClaims}
                    />

                    <ExpenseAttachmentsCard
                      claimId={claimId}
                      items={itemsForAttach}
                      onEnsureClaim={saveClaim}
                      onChanged={loadMyClaims}
                    />

                    <ClaimSummaryCard
                      claimId={claimId}
                      onEnsureClaim={saveClaim}
                    />
                  </div>
                ) : (
                  <UserExpenseClaimsHistoryCard
                    claims={myClaims}
                    loading={loadingMyClaims}
                    onRefresh={loadMyClaims}
                  />
                )}
              </div>
            </div>
          </>
        )}
      </main>
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="colored"
      />
    </section>
  );
}
